import React, { PropsWithChildren, ReactNode, createElement } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { RatingStrategy } from '@/api/copy-trading/types';
import { CheckCircle } from '@/blocks/components/Icons';
import { SparklineChart } from '@/blocks/components/SparklineChart';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { WithClassName } from '@/blocks/components/types';
import { FormatPercent } from '@/blocks/formatters';
import { LinkI18n } from '@/i18n/routing';
import buttons from '@/styles/button.module.scss';
import context from '@/styles/contexts.module.scss';
import typography from '@/styles/typography.module.scss';

import { getStrategyPageLink } from '@/app/[lang]/copy-trade/getStrategyPageLink';

import { StrategyAvatar } from '../StrategyAvatar';
import styles from './TradersCard.module.scss';

type BadgeProps = {
  stars: number;
};

const StarSVG = () => (
  <svg
    className={styles.star}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.4445 2.76855C8.35833 2.6034 8.18742 2.5 8.00072 2.5C7.81401 2.5 7.64454 2.6034 7.55694 2.76855L6.01303 5.72115L2.66815 6.27692C2.48432 6.30708 2.33209 6.43776 2.27464 6.6144C2.21719 6.79104 2.26171 6.98635 2.39241 7.11991L4.77648 9.55552L4.2695 12.926C4.24222 13.1113 4.31977 13.2965 4.47057 13.4057C4.62281 13.5148 4.82244 13.5306 4.98903 13.4459L8.00072 11.9208L11.0124 13.4459C11.179 13.5306 11.3801 13.5148 11.5309 13.4057C11.6831 13.2965 11.7607 13.1113 11.7319 12.926L11.2235 9.55695L13.6076 7.12134C13.7383 6.98779 13.7828 6.79391 13.7254 6.61584C13.6679 6.4392 13.5157 6.30851 13.3318 6.27836L9.98697 5.72259L8.4445 2.76855Z"
      fill="currentColor"
    />
  </svg>
);

export function StrategyStatItemsContainer({ children }: PropsWithChildren) {
  return <div className={styles.cardStrategyProgressionWrapper}>{children}</div>;
}

export function StrategyStatChip({ name, value }: { name: ReactNode; value: ReactNode }) {
  return (
    <div className={styles.cardStrategyProgressionItem}>
      <span className={classNames(typography.bodyXS, styles.strategyChipName)}>{name}</span>
      <span className={classNames(typography.bodyS, styles.strategyChipValue)}>{value}</span>
    </div>
  );
}

const StarsBadge = ({ stars = 0 }: BadgeProps) => {
  const badgeScheme = stars === 5 ? styles.badgeDark : styles.badgeLight;

  const twoStarsLayers = stars >= 4;
  const firstLayerStarsAmount = stars === 4 ? 2 : 3;

  return (
    <div className={classNames(styles.badge, badgeScheme)}>
      <svg
        className={styles.badgeContour}
        width="68"
        height="69"
        viewBox="0 0 68 69"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 46.7173V6C0 2.68629 2.68629 0 6 0H62C65.3137 0 68 2.68629 68 6V46.7944C68 48.9619 66.831 50.9609 64.9419 52.0237L37.6436 67.3816C35.8524 68.3893 33.67 68.4101 31.8599 67.4367L3.15821 52.0016C1.21319 50.9556 0 48.9257 0 46.7173Z"
          fill="currentColor"
        />
      </svg>
      <div className={styles.badgeContent}>
        {twoStarsLayers ? (
          <>
            <div className={styles.badgeStars}>
              {Array(firstLayerStarsAmount)
                .fill('star')
                .map((_, index) => (
                  <StarSVG key={index} />
                ))}
            </div>
            <div className={styles.badgeStars}>
              {Array(stars - firstLayerStarsAmount)
                .fill('star')
                .map((_, index) => (
                  <StarSVG key={index} />
                ))}
            </div>
          </>
        ) : (
          <div className={styles.badgeStars}>
            {Array(stars)
              .fill('star')
              .map((_, index) => (
                <StarSVG key={index} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

function StrategyCardStatsChipsBlock({ children }: PropsWithChildren) {
  const tr = useTranslations('pages.copyTrading.topTraders.sections.tops');

  return (
    <div className={styles.cardStrategy}>
      <p className={classNames(typography.bodyS, styles.cardStrategyName)}>
        {tr.rich('strategyLossAndProfit')}
      </p>
      <StrategyStatItemsContainer>{children}</StrategyStatItemsContainer>
    </div>
  );
}

function StrategyCardStatsRowBlock({ children }: PropsWithChildren) {
  return <div className={styles.cardAdditionalInfo}>{children}</div>;
}

function StrategyCardStatsRowBlockItem({ children }: PropsWithChildren) {
  return <div className={styles.cardAdditionalInfoItem}>{children}</div>;
}

function WrapLongText({
  children,
  className,
  tag = 'span',
}: PropsWithChildren<{ tag?: string } & WithClassName>) {
  return createElement(tag, { className: classNames(className) }, children);
}

export function TradersCard(props: RatingStrategy & { showAwardsBadge?: boolean }) {
  const tr = useTranslations();
  const {
    strategyName,
    strategyId,
    verified,
    dailyYield,
    totalYield,
    achievements,
    performance,
    showAwardsBadge = false,
  } = props;
  const starsAmount = Object.values(achievements).filter((trueStatement) => trueStatement).length;

  return (
    <div className={classNames(styles.cardWrapper, context.light)}>
      {showAwardsBadge ? <StarsBadge stars={starsAmount} /> : null}

      <div className={styles.cardAccount}>
        <StrategyAvatar strategyId={strategyId} className={styles.cardAccountAvatar} />
        <div className={styles.cardAccountInfo}>
          <div className={styles.cardAccountInfoCredetials}>
            <p className={classNames(typography.subheadingM, styles.cardAccountInfoName)}>{strategyName}</p>
            {verified && <CheckCircle />}
          </div>
        </div>
      </div>

      <StrategyCardStatsChipsBlock>
        {dailyYield ? (
          <StrategyStatChip
            name={tr.rich('pages.copyTrading.topTraders.sections.tops.dailyYield')}
            value={
              <WithTrendColor valueToCalculateTrend={dailyYield}>
                <FormatPercent value={dailyYield} />
              </WithTrendColor>
            }
          />
        ) : null}
      </StrategyCardStatsChipsBlock>

      <div className={styles.cardFooter}>
        <LinkI18n
          className={classNames(buttons.btn, buttons.btnS, buttons.btnPrimary, styles.cardViewProfileBtn)}
          href={getStrategyPageLink(strategyId)}
          data-event-label={tr.rich('en.pages.copyTrading.topTraders.sections.tops.cta')}
        >
          {tr.rich('pages.copyTrading.topTraders.sections.tops.cta')}
        </LinkI18n>
        <StrategyCardStatsRowBlock>
          <StrategyCardStatsRowBlockItem>
            <WrapLongText>
              <WithTrendColor
                className={classNames(typography.subheadingM, typography.semibold)}
                valueToCalculateTrend={totalYield}
              >
                <FormatPercent value={totalYield} />
              </WithTrendColor>
            </WrapLongText>
            <span className={classNames(typography.bodyS)}>
              {tr.rich('pages.copyTrading.topTraders.sections.tops.totalYield')}
            </span>
          </StrategyCardStatsRowBlockItem>
        </StrategyCardStatsRowBlock>
        <SparklineChart chartValues={performance} autosize className={styles.chart} />
      </div>
    </div>
  );
}
